import type {Type, UnknownId, RigidId} from './DataTypeDefinition';
import type {Qualified, TypeName} from '@packages/systems/wfdl-Qualified';

type TypeMatcher<o> = {
  Element: (arg1: undefined, arg2: Type) => o;
  Generic: (
    arg1: {
      arg: string;
      body: Type;
    },
    arg2: Type
  ) => o;
  Rigid: (arg1: RigidId, arg2: Type) => o;
  RowCons: (
    arg1: {
      label: string;
      type: Type;
      tail: Type;
    },
    arg2: Type
  ) => o;
  RowEmpty: (arg1: undefined, arg2: Type) => o;
  TypeApplication: (
    arg1: {
      con: Type;
      arg: Type;
    },
    arg2: Type
  ) => o;
  TypeConstructor: (arg1: Qualified<TypeName>, arg2: Type) => o;
  TypeReference: (arg1: string, arg2: Type) => o;
  Unknown: (
    arg1: {
      id: UnknownId;
      rigids: Record<RigidId, true>;
    },
    arg2: Type
  ) => o;
};

type PartialTypeMatcher<o> = Partial<TypeMatcher<o>> & {
  _: (arg1: Type) => o;
};

export function matchType<o>(matchers: TypeMatcher<o> | PartialTypeMatcher<o>) {
  return (dataType: Type): o => {
    const matcher = matchers[dataType.type];

    // @ts-expect-error its a thing
    if (matcher) return matcher(dataType.val, dataType);

    // @ts-expect-error its a thing
    const fallback = matchers._;
    if (!fallback) {
      throw new TypeError(
        `matchType received a data type of type "${dataType.type}" but no corresponding matcher function (or a default _) was provided.`
      );
    }
    return fallback(dataType);
  };
}
/**
 * Returns type property on dataType
 * Note: This is defined in `matchType` instead of `DataTypeDefinition`, as otherwise, we get a circular dependency
 */
export const getTypeType = matchType({
  Element: (_) => 'Element',
  Generic: (_) => 'Generic',
  Rigid: (_) => 'Rigid',
  RowCons: (_) => 'RowCons',
  RowEmpty: (_) => 'RowEmpty',
  TypeApplication: (_) => 'TypeApplication',
  TypeConstructor: (_) => 'TypeConstructor',
  TypeReference: (_) => 'TypeReference',
  Unknown: (_) => 'Unknown',
});
